import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";

const MUSIC_ITEMS = [
  { label: "Audio Branding", href: "/music/audio-branding" },
  { label: "Series, Cine y TV", href: "/music/series-cine-tv" },
  { label: "Producción Artística", href: "/music/produccion-artistica" },
];

const TEAMS_ITEMS = [
  { label: "Cuenta Conmigo", href: "/teams/cuenta-conmigo" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMusic, setOpenMusic] = useState(false);
  const [openTeams, setOpenTeams] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMusicOpen, setMobileMusicOpen] = useState(false);
  const [mobileTeamsOpen, setMobileTeamsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Close menus on route change
    setMobileOpen(false);
    setOpenMusic(false);
    setOpenTeams(false);
    setMobileMusicOpen(false);
    setMobileTeamsOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled || !isHome || mobileOpen ? "bg-black/80 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="relative flex items-center justify-between px-5 md:px-10 py-4 md:py-5">
        {/* Mobile: menu button on the left */}
        <button
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white hover:bg-white/10 transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Logo center on desktop, right on mobile */}
        <Link to="/" className="md:absolute md:left-1/2 md:-translate-x-1/2 flex items-center group" data-testid="header-logo">
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/3ti1wpm1_color-clanco-apaisado.webp"
            alt="Kilombo"
            className="h-8 md:h-10 w-auto object-contain group-hover:opacity-90 transition-opacity"
            loading="eager"
            decoding="async"
          />
        </Link>

        <div className="w-10 md:hidden" />

        {/* Desktop nav */}
        <nav className="ml-auto hidden md:flex items-center gap-1 text-sm font-mono-caps text-white/85">
          {/* Music dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMusic(true)}
            onMouseLeave={() => setOpenMusic(false)}
          >
            <Link
              to="/music"
              className="flex items-center gap-1 px-3 py-2 uppercase tracking-widest hover:text-[#ff8a3c] transition-colors"
            >
              Music <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMusic ? "rotate-180" : ""}`} />
            </Link>
            <div
              className={`absolute left-0 top-full pt-2 min-w-[240px] transition-all duration-200 ${
                openMusic ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
            >
              <div className="rounded-xl border border-white/10 bg-black/95 backdrop-blur-md shadow-xl overflow-hidden">
                {MUSIC_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block px-4 py-3 text-white/80 hover:text-[#ff8a3c] hover:bg-white/5 transition-colors text-[13px] tracking-widest uppercase"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Teams dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenTeams(true)}
            onMouseLeave={() => setOpenTeams(false)}
          >
            <Link
              to="/teams"
              className="flex items-center gap-1 px-3 py-2 uppercase tracking-widest hover:text-[#2ec9a8] transition-colors"
            >
              Teams <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openTeams ? "rotate-180" : ""}`} />
            </Link>
            <div
              className={`absolute left-0 top-full pt-2 min-w-[220px] transition-all duration-200 ${
                openTeams ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
            >
              <div className="rounded-xl border border-white/10 bg-black/95 backdrop-blur-md shadow-xl overflow-hidden">
                {TEAMS_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block px-4 py-3 text-white/80 hover:text-[#2ec9a8] hover:bg-white/5 transition-colors text-[13px] tracking-widest uppercase"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/nosotros" className="px-3 py-2 uppercase tracking-widest hover:text-white transition-colors">
            Nosotros
          </Link>
          <Link
            to="/contact"
            className="ml-2 px-4 py-2 rounded-full border border-white/25 hover:border-[#ff8a3c] hover:text-[#ff8a3c] transition-colors uppercase tracking-widest"
          >
            Contacto
          </Link>
        </nav>
      </div>

      {/* Mobile menu drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-300 ${
          mobileOpen ? "max-h-[600px] border-t border-white/5" : "max-h-0"
        }`}
      >
        <nav className="px-5 py-4 flex flex-col gap-1 bg-black/95">
          <div>
            <button
              onClick={() => setMobileMusicOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white/90 hover:bg-white/5 font-mono-caps uppercase tracking-widest text-sm"
            >
              <span>Music</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileMusicOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`overflow-hidden transition-[max-height] duration-200 ${mobileMusicOpen ? "max-h-[300px]" : "max-h-0"}`}>
              <div className="pl-4 py-1">
                <Link to="/music" className="block px-4 py-2.5 text-white/70 hover:text-[#ff8a3c] text-sm tracking-wider uppercase">Ver todo Music</Link>
                {MUSIC_ITEMS.map((item) => (
                  <Link key={item.href} to={item.href} className="block px-4 py-2.5 text-white/70 hover:text-[#ff8a3c] text-sm tracking-wider uppercase">{item.label}</Link>
                ))}
              </div>
            </div>
          </div>
          <div>
            <button
              onClick={() => setMobileTeamsOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-white/90 hover:bg-white/5 font-mono-caps uppercase tracking-widest text-sm"
            >
              <span>Teams</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileTeamsOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`overflow-hidden transition-[max-height] duration-200 ${mobileTeamsOpen ? "max-h-[300px]" : "max-h-0"}`}>
              <div className="pl-4 py-1">
                <Link to="/teams" className="block px-4 py-2.5 text-white/70 hover:text-[#2ec9a8] text-sm tracking-wider uppercase">Ver todo Teams</Link>
                {TEAMS_ITEMS.map((item) => (
                  <Link key={item.href} to={item.href} className="block px-4 py-2.5 text-white/70 hover:text-[#2ec9a8] text-sm tracking-wider uppercase">{item.label}</Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/nosotros" className="px-4 py-3 rounded-lg text-white/90 hover:bg-white/5 hover:text-white font-mono-caps uppercase tracking-widest text-sm">
            Nosotros
          </Link>
          <Link to="/contact" className="mt-2 mx-4 mb-2 px-4 py-3 rounded-full text-center border border-white/25 hover:border-[#ff8a3c] hover:text-[#ff8a3c] font-mono-caps uppercase tracking-widest text-sm">
            Contacto
          </Link>
        </nav>
      </div>
    </header>
  );
}
